import type { FederatedPointerEvent, Container } from 'pixi.js';

export class CheatsEnabler {
  private readonly pointerEventsHistory: { pointerId: number; type: 'down' | 'up'; x: number }[] = [];

  constructor(
    private readonly lifeCycleObject: Container,
    private readonly activateCheats: () => void,
  ) {
    lifeCycleObject.pixi.stage.on('pointerdown', this.onPointerDown);
    lifeCycleObject.pixi.stage.on('pointerup', this.onPointerUp);
    window.document.addEventListener('keydown', this.onKeyDown);
    window.document.addEventListener('keyup', this.onKeyUp);

    lifeCycleObject.once('destroyed', () => {
      this.destroy();
    });
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    if (event.key === 'ArrowLeft') {
      this.pointerEventsHistory.push({ pointerId: 0, type: 'down', x: -100 });
    } else if (event.key === 'ArrowDown') {
      this.pointerEventsHistory.push({ pointerId: 1, type: 'down', x: 0 });
    } else if (event.key === 'ArrowRight') {
      this.pointerEventsHistory.push({ pointerId: 2, type: 'down', x: 100 });
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    if (event.key === 'ArrowLeft') {
      this.pointerEventsHistory.push({ pointerId: 0, type: 'up', x: -100 });
    } else if (event.key === 'ArrowDown') {
      this.pointerEventsHistory.push({ pointerId: 1, type: 'up', x: 0 });
    } else if (event.key === 'ArrowRight') {
      this.pointerEventsHistory.push({ pointerId: 2, type: 'up', x: 100 });
    }

    this.tryActivateCheats();
  };

  private readonly onPointerDown = (event: FederatedPointerEvent): void => {
    this.pointerEventsHistory.push({ pointerId: event.pointerId, type: 'down', x: event.data.global.x });
  };

  private readonly onPointerUp = (event: FederatedPointerEvent): void => {
    this.pointerEventsHistory.push({ pointerId: event.pointerId, type: 'up', x: event.data.global.x });

    this.tryActivateCheats();
  };

  private tryActivateCheats(): void {
    if (!this.shouldEnableCheats()) return;
    this.activateCheats();
    this.destroy();
  }

  private shouldEnableCheats(): boolean {
    const input = this.pointerEventsHistory.slice(-6);
    if (input.length < 6) return false;

    const properTypes =
      input[0].type === 'down' &&
      input[1].type === 'down' &&
      input[2].type === 'down' &&
      input[3].type === 'up' &&
      input[4].type === 'up' &&
      input[5].type === 'up';

    const properPointers =
      input[0].pointerId === input[3].pointerId &&
      input[1].pointerId === input[4].pointerId &&
      input[2].pointerId === input[5].pointerId;

    const properSpacing = input[0].x < input[1].x && input[2].x > input[0].x && input[2].x < input[1].x;

    return properTypes && properSpacing && properPointers;
  }

  private destroy(): void {
    this.lifeCycleObject.pixi.stage.off('pointerdown', this.onPointerDown);
    this.lifeCycleObject.pixi.stage.off('pointerup', this.onPointerUp);
    window.document.removeEventListener('keydown', this.onKeyDown);
    window.document.removeEventListener('keyup', this.onKeyUp);
  }
}
